// src/views/Admin/Locales/LocalPage/components/CreatePuestoModal/CreatePuestoModal.tsx

import React, { useState, useEffect } from 'react';
import { HiOutlineBriefcase, HiOutlineXMark } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { createPuesto } from '../../../../../../crud/management_api/puestos';
import './CreatePuestoModal.scss';

interface CreatePuestoModalProps {
  isOpen: boolean;
  localId: number;
  localName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePuestoModal: React.FC<CreatePuestoModalProps> = ({
  isOpen,
  localId,
  localName,
  onClose,
  onSuccess
}) => {
  const [puestoName, setPuestoName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPuestoName('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!puestoName.trim()) {
      setError('El nombre del puesto es requerido');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createPuesto({
        puesto_name: puestoName.trim(),
        local_id: localId
      });
      toast.success('Puesto creado exitosamente');
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Error al crear el puesto';
      setError(typeof message === 'string' ? message : 'Error al crear el puesto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-puesto-modal-overlay" onClick={handleClose}>
      <div 
        className="create-puesto-modal" 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <button className="close-button" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-icon">
          <HiOutlineBriefcase size={28} />
        </div>

        <h2>Nuevo Puesto de Trabajo</h2>
        <p className="modal-subtitle">
          Para <strong>{localName}</strong>
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>Nombre del Puesto *</label>
          <input
            type="text"
            value={puestoName}
            onChange={(e) => setPuestoName(e.target.value)}
            placeholder="Ej: Mesero, Cajero, Chef, Bartender..."
            disabled={isSubmitting}
            autoFocus
          />
        </div>

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" />
                Creando...
              </>
            ) : (
              <>
                <HiOutlineBriefcase size={18} />
                Crear Puesto
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePuestoModal;
